// After a deploy rotates chunk hashes, a still-open tab's dynamic `import()`
// (a route lazy-load, a code-split component, or a locale catalog chunk)
// tries to fetch a file the server no longer has and rejects with the
// browser's standard chunk-fetch TypeError. A single full reload pulls the
// fresh index.html and its current chunk hashes.
//
// If the fresh bundle fails the same way (a deploy that produced a
// self-inconsistent bundle, or a chunk the network keeps failing), reloading
// again would loop forever, so the reload is guarded across reloads: it is
// recorded in sessionStorage, which survives the reload, and while that
// record is younger than STALE_BUNDLE_RELOAD_GUARD_MS the failure is left to
// the error UI. The record expires rather than lasting the tab's lifetime, so
// a later deploy in the same tab can still recover with a reload of its own.
// TanStack Router's own lazy loader guards its reload the same way.

const RELOAD_GUARD_KEY = 'leanin.stale-bundle-reload-at';

// Long enough to cover a reload, the boot's font and Clerk gates and the
// first navigations after it, where a broken bundle fails again; short next
// to the time between deploys.
export const STALE_BUNDLE_RELOAD_GUARD_MS = 5 * 60_000;

export interface StaleBundleReloadEnvironment {
  // Session-scoped storage that degrades instead of throwing
  // (browser-storage.ts): a read of blocked storage is null, a write reports
  // whether it took effect.
  readonly read: (key: string) => string | null;
  readonly write: (key: string, value: string) => boolean;
  readonly now: () => number;
  readonly reload: () => void;
}

// Returns the reload to run on a stale-bundle failure; it reloads at most once
// per guard window.
export function staleBundleReload(env: StaleBundleReloadEnvironment): () => void {
  return () => {
    const now = env.now();
    const lastReloadAt = Number(env.read(RELOAD_GUARD_KEY));
    const sinceLastReload = now - lastReloadAt;
    // A record from the future (the clock was set back) guards nothing, or it
    // could block every reload for as long as the clock was moved.
    if (lastReloadAt > 0 && sinceLastReload >= 0 && sinceLastReload < STALE_BUNDLE_RELOAD_GUARD_MS) {
      return;
    }
    // Without a record that survives the reload nothing could stop a loop, so
    // an unwritable guard (storage blocked) leaves the failure to the error UI.
    if (!env.write(RELOAD_GUARD_KEY, String(now))) return;
    env.reload();
  };
}

// The TypeError text each engine emits when a dynamic import's chunk fetch
// 404s; the wording is per-engine, not standardized. Matching on them
// distinguishes a stale-bundle failure (retry by reloading) from an
// application error (surface / log it).
const CHUNK_FETCH_FAILURES = [
  'Failed to fetch dynamically imported module', // Chromium
  'Importing a module script failed', // WebKit / Safari
  'error loading dynamically imported module', // Firefox
];

export function isStaleBundleError(reason: unknown): boolean {
  const message =
    reason !== null && typeof reason === 'object' && 'message' in reason
      ? String((reason as { message: unknown }).message)
      : String(reason);
  return CHUNK_FETCH_FAILURES.some((failure) => message.includes(failure));
}
